import { z } from 'zod';
import { listDateBoundSchema, listSortSchema } from '../listQuery.js';
import { booleanStringSchema, crossServerEnforcementRefinement, hasAtMostOneScope, paginationSchema, scopeAllowsCrossServerEnforcement, scopeRefinement, uuidSchema, violationSeveritySchema, } from '../schemas.js';
import { ACTIONS, automationActionsSchema } from './actions.js';
import { CONDITION_FIELDS, automationConditionsSchema } from './conditions.js';
import { NOTIFICATION_ONLY_TRIGGERS, TRIGGERS, TRIGGER_GROUPS, contextOf, contextSupplies, resolveVariable, triggerNodeSchema, variablesFor, } from './triggers.js';
import { parseTemplate, templateVariables } from './template.js';
export const AUTOMATION_KINDS = ['policy', 'notification'];
// 'error' belongs to a run that failed before its terminal write; a bookkeeping failure
// after that write notes itself in steps rather than demoting the row.
export const RUN_OUTCOMES = ['completed', 'stopped_by_condition', 'error'];
/** Days a completed run survives when its automation names no retention of its own. */
export const RETENTION_DEFAULTS = {
    policy: 365,
    notification: 30,
};
/** The automations column, and what a name has to fit into. */
export const AUTOMATION_NAME_MAX = 100;
/** The note beside the name, capped the same way the column is. */
export const AUTOMATION_DESCRIPTION_MAX = 500;
const automationFieldsSchema = z.strictObject({
    name: z.string().trim().min(1).max(AUTOMATION_NAME_MAX),
    description: z.string().max(AUTOMATION_DESCRIPTION_MAX).nullable().optional(),
    kind: z.enum(AUTOMATION_KINDS),
    severity: violationSeveritySchema.nullable(),
    triggers: z.array(triggerNodeSchema),
    conditions: automationConditionsSchema,
    actions: automationActionsSchema,
    serverId: uuidSchema.nullable().optional(),
    serverUserId: uuidSchema.nullable().optional(),
    userId: uuidSchema.nullable().optional(),
    enforceAcrossServers: z.boolean().optional(),
    cooldownMinutes: z.number().int().min(0).nullable().optional(),
    retentionDays: z.number().int().min(1).max(3650).nullable().optional(),
    isActive: z.boolean().optional(),
});
function valueMatches(descriptor, operator, value) {
    if (operator === 'in' || operator === 'not_in')
        return Array.isArray(value);
    switch (descriptor.valueType) {
        case 'number':
            return typeof value === 'number';
        case 'boolean':
            return typeof value === 'boolean';
        // `in`/`not_in` returned above; what is left on a list field is one of its options.
        case 'multiSelect':
            return typeof value === 'string';
        default:
            return typeof value === 'string';
    }
}
/**
 * Everything a definition has to satisfy beyond the node schemas: what the triggers
 * can supply is what the conditions, actions and message variables may name.
 */
function definitionRefinements(def, ctx) {
    const { triggers } = def;
    const enabled = triggers.filter((trigger) => trigger.enabled);
    if (enabled.length === 0) {
        ctx.addIssue({
            code: 'custom',
            path: ['triggers'],
            message: 'At least one enabled trigger is required',
        });
    }
    const context = contextOf(triggers);
    if (def.kind === 'policy' &&
        enabled.some((trigger) => !contextSupplies(TRIGGERS[trigger.type].context, 'account'))) {
        ctx.addIssue({
            code: 'custom',
            path: ['triggers'],
            message: 'A policy needs a stream or account trigger: violations are about a user',
        });
    }
    // A session context supplies an account, so the rule above would let a policy sit on a new device.
    if (def.kind === 'policy' &&
        enabled.some((trigger) => NOTIFICATION_ONLY_TRIGGERS.includes(trigger.type))) {
        ctx.addIssue({
            code: 'custom',
            path: ['triggers'],
            message: 'New device and trust score changed can only send: neither is something a person did wrong',
        });
    }
    // A trust action one hop from the writer that fired the trigger walks the score down on every run.
    const watchesTrust = enabled.some((trigger) => trigger.type === 'account.trust_changed');
    const checkGroups = (groups, path) => {
        groups.forEach((group, groupIndex) => group.conditions.forEach((condition, conditionIndex) => {
            const descriptor = CONDITION_FIELDS[condition.field];
            const at = [...path, groupIndex, 'conditions', conditionIndex];
            if (condition.enabled !== false &&
                context !== null &&
                !contextSupplies(context, descriptor.requires)) {
                ctx.addIssue({
                    code: 'custom',
                    path: [...at, 'field'],
                    message: `${condition.field} is not available for every enabled trigger`,
                });
            }
            if (!descriptor.operators.includes(condition.operator)) {
                ctx.addIssue({
                    code: 'custom',
                    path: [...at, 'operator'],
                    message: `${condition.operator} is not valid for ${condition.field}`,
                });
            }
            if (!valueMatches(descriptor, condition.operator, condition.value)) {
                ctx.addIssue({
                    code: 'custom',
                    path: [...at, 'value'],
                    message: `value does not fit ${condition.field}`,
                });
            }
        }));
    };
    checkGroups(def.conditions.groups, ['conditions', 'groups']);
    const vars = new Set(variablesFor(triggers));
    const checkAction = (action, path) => {
        if (action.enabled !== false) {
            const need = ACTIONS[action.type].requires;
            if (context !== null && !contextSupplies(context, need)) {
                ctx.addIssue({
                    code: 'custom',
                    path: [...path, 'type'],
                    message: `${action.type} needs a ${need} trigger`,
                });
            }
            if (watchesTrust && action.type === 'trust') {
                ctx.addIssue({
                    code: 'custom',
                    path: [...path, 'type'],
                    message: 'An automation that watches trust cannot also change it',
                });
            }
        }
        if (action.type !== 'send')
            return;
        for (const field of ['title', 'body']) {
            const text = action[field];
            if (text === undefined)
                continue;
            const parsed = parseTemplate(text);
            if (!parsed.ok) {
                ctx.addIssue({
                    code: 'custom',
                    path: [...path, field],
                    message: `template error ${parsed.error.code} at line ${parsed.error.line}`,
                    params: { kind: 'templateParse', ...parsed.error },
                });
                continue;
            }
            for (const name of templateVariables(parsed.nodes)) {
                if (!vars.has(resolveVariable(name))) {
                    ctx.addIssue({
                        code: 'custom',
                        path: [...path, field],
                        message: `{{${name}}} is not available for every enabled trigger`,
                        params: { kind: 'templateVariable', name },
                    });
                }
            }
        }
    };
    def.actions.actions.forEach((action, index) => {
        const path = ['actions', 'actions', index];
        checkAction(action, path);
        if (action.type === 'if' && action.enabled !== false) {
            checkGroups(action.conditions.groups, [...path, 'conditions', 'groups']);
            action.then.forEach((leaf, leafIndex) => checkAction(leaf, [...path, 'then', leafIndex]));
            action.else.forEach((leaf, leafIndex) => checkAction(leaf, [...path, 'else', leafIndex]));
        }
    });
}
export const automationDefinitionSchema = automationFieldsSchema.superRefine(definitionRefinements);
export const createAutomationSchema = automationDefinitionSchema
    .refine(hasAtMostOneScope, scopeRefinement)
    .refine(scopeAllowsCrossServerEnforcement, crossServerEnforcementRefinement);
export const updateAutomationSchema = automationFieldsSchema
    .partial()
    .refine(hasAtMostOneScope, scopeRefinement)
    .refine(scopeAllowsCrossServerEnforcement, crossServerEnforcementRefinement);
export const AUTOMATION_SORT_FIELDS = [
    'name',
    'createdAt',
    'updatedAt',
    'kind',
    'isActive',
];
/** The three ways a template arrives; a row no template wrote is the fourth answer. */
const TEMPLATE_SOURCES = ['builtin', 'import', 'local'];
export const AUTOMATION_SOURCES = [...TEMPLATE_SOURCES, 'own'];
export const automationListQuerySchema = paginationSchema
    .extend({
    kind: z.enum(AUTOMATION_KINDS).optional(),
    enabled: booleanStringSchema.optional(),
    search: z.string().trim().min(1).max(100).optional(),
    source: z.enum(AUTOMATION_SOURCES).optional(),
    serverId: uuidSchema.optional(),
    trigger: z.enum(TRIGGER_GROUPS).optional(),
    severity: violationSeveritySchema.optional(),
})
    .extend(listSortSchema(AUTOMATION_SORT_FIELDS).shape);
export const RUN_SORT_FIELDS = ['startedAt', 'finishedAt', 'outcome'];
/** What a page of runs and a count of them filter on, so neither can drift. */
export const runCountsQuerySchema = z.object({
    kind: z.enum(AUTOMATION_KINDS).optional(),
    outcome: z.enum(RUN_OUTCOMES).optional(),
    automationId: uuidSchema.optional(),
    // Calendar days against the run's start, resolved to half-open UTC bounds.
    startDate: listDateBoundSchema,
    endDate: listDateBoundSchema,
});
export const runListQuerySchema = paginationSchema
    .extend(runCountsQuerySchema.shape)
    .extend(listSortSchema(RUN_SORT_FIELDS).shape);
export const NEAR_MISS_REASONS = [
    'cooldown_active',
    'edge_replayed',
    'gate_blocked',
    'trigger_filter_failed',
];
/** One entry of the capped ring: a trigger matched but the pipeline recorded no run. */
export const nearMissEntrySchema = z.object({
    reason: z.enum(NEAR_MISS_REASONS),
    at: z.iso.datetime(),
    subjectKey: z.string(),
    trigger: z.string(),
});
//# sourceMappingURL=definition.js.map